import { useEchartsContainerWidth } from '@app/src/hooks/useEchartsContainerWidth'
import { usePeriodName } from '@app/src/hooks/usePeriodName'
import { ChartType } from '@app/src/pages/Assessments/ChartTypeSelector'
import {
  DONT_HAVE_THIS_COLOR,
  notAnsweredStyling,
} from '@app/src/pages/ResourceCollection/Collections/DataHub/DataInsights/Charts/CategoryChartsConfigs/categoryChartConfigsUtils'
import type { StatisticsSummary } from '@app/src/pages/ResourceCollection/Collections/DataHub/DataInsights/Charts/DataHubSummaryChart'
import type { CartesianAxisOption } from '@app/src/types/echarts'
import { getMinMaxOnYAxisCountCharts, getVisualizationColorsMaxContrast } from '@app/src/utils/statisticsUtils'
import { darken, useTheme } from '@mui/material'
import type { BarSeriesOption, EChartsOption } from 'echarts'
import type ReactEChartsCore from 'echarts-for-react/lib/core'
import type { XAXisOption, YAXisOption } from 'echarts/types/dist/shared'
import type React from 'react'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { OptionResultItemMultiPeriod } from '../../../../DataHubScene'
import { getLegendSpacing } from '../chartsUtils'
import { SummaryChartDataTypes } from './usePeriodSummaryChartConfigs'

interface OptionMultiPeriodChartConfigsParams {
  optionItemResult: { groupName: string; options: OptionResultItemMultiPeriod[] }[]
  statisticsSummary: StatisticsSummary
  selectedChartType: ChartType
  itemsInOther: string[]
  echartsRef: React.MutableRefObject<ReactEChartsCore | null>
}

const APPROXIMATE_WIDTH_OF_YAXIS = 60

const getAxisLabelWidth = (widthOfChart: number | undefined, existingOptions: string[]) => {
  if (!widthOfChart) return undefined
  return (widthOfChart - APPROXIMATE_WIDTH_OF_YAXIS) / existingOptions.length
}

const getMinMaxYAxis = (
  optionItemResult: { options: OptionResultItemMultiPeriod[] }[],
  statisticsSummary: StatisticsSummary,
) => {
  const optionValues = optionItemResult.flatMap(op => op.options.map(op => op.count))
  const summaryValues = statisticsSummary.resultForGroups.flatMap(res => [
    res.companiesAnsweredDontHaveThis,
    res.companiesNotAnswered,
  ])
  return getMinMaxOnYAxisCountCharts([...optionValues, ...summaryValues])
}

export const useOptionMultiPeriodChartConfigs = ({
  optionItemResult,
  statisticsSummary,
  selectedChartType,
  itemsInOther,
  echartsRef,
}: OptionMultiPeriodChartConfigsParams) => {
  const { formatMessage } = useIntl()
  const { typography, palette } = useTheme()
  const widthOfChart = useEchartsContainerWidth(echartsRef)
  const isHorizontalBarChart = selectedChartType === ChartType.Horizontal
  const { formatPeriodName } = usePeriodName()

  const existingOptions = optionItemResult?.[0]?.options.map(op => op.name)
  const colors = getVisualizationColorsMaxContrast(existingOptions?.length || 0)

  const { min, max } = useMemo(
    () => getMinMaxYAxis(optionItemResult, statisticsSummary),
    [optionItemResult, statisticsSummary],
  )

  const categoryAxis: CartesianAxisOption = useMemo(
    () => ({
      type: 'category',
      data: optionItemResult.map(item => formatPeriodName(item.groupName)),
      triggerEvent: true,
      axisLabel: {
        interval: 0,
        width: getAxisLabelWidth(
          widthOfChart,
          optionItemResult.map(i => i.groupName),
        ),
        overflow: 'break',
        fontFamily: typography.fontFamily,
        fontSize: typography.body2.fontSize,
      },
    }),
    [existingOptions, optionItemResult, typography.body2.fontSize, typography.fontFamily],
  )

  const valueAxis: CartesianAxisOption = useMemo(
    () => ({
      type: 'value',
      name: formatMessage({ id: 'statistics.dataInsights.count' }),
      nameLocation: 'middle',
      nameGap: 35,
      min: min,
      max: max,
      minInterval: 1,
      nameTextStyle: {
        fontFamily: typography.fontFamily,
        fontSize: typography.body2.fontSize,
      },
    }),
    [formatMessage, typography.body2.fontSize, typography.fontFamily, min, max],
  )

  const questionSkippedSeries: BarSeriesOption = useMemo(
    () => ({
      name: formatMessage({ id: 'statistics.dataInsights.dontHaveThis' }),
      type: 'bar',
      barCategoryGap: '35%',
      emphasis: { itemStyle: { color: darken(DONT_HAVE_THIS_COLOR, 0.2) } },
      data: optionItemResult.map(g => {
        const summaryData = statisticsSummary.resultForGroups.find(s => s.groupName === g.groupName)
        return {
          value: summaryData?.companiesAnsweredDontHaveThis || 0,
          name: formatPeriodName(g.groupName),
          groupName: g.groupName,
          summaryType: SummaryChartDataTypes.CompaniesAnsweredDontHaveThis,
          answersSelected: [],
        }
      }),
      color: DONT_HAVE_THIS_COLOR,
    }),
    [
      formatMessage,
      optionItemResult,
      statisticsSummary,
      formatPeriodName,
      isHorizontalBarChart,
      typography,
      widthOfChart,
    ],
  )

  const noResponseSeries: BarSeriesOption = useMemo(
    () => ({
      name: formatMessage({ id: 'statistics.dataInsights.noResponse' }),
      type: 'bar',
      barCategoryGap: '35%',
      ...notAnsweredStyling(palette),
      data: optionItemResult.map(g => {
        const summaryData = statisticsSummary.resultForGroups.find(s => s.groupName === g.groupName)
        return {
          value: summaryData?.companiesNotAnswered || 0,
          name: formatPeriodName(g.groupName),
          groupName: g.groupName,
          summaryType: SummaryChartDataTypes.CompaniesNotAnswered,
          answersSelected: [],
          ...(!isHorizontalBarChart && {
            label: {
              position: 'top',
            },
          }),
        }
      }),
    }),
    [formatMessage, optionItemResult, statisticsSummary, formatPeriodName, isHorizontalBarChart, palette],
  )

  const optionSeries: BarSeriesOption[] = useMemo(
    () =>
      existingOptions?.map((option, optionIndex) => ({
        name: option,
        type: 'bar',
        barCategoryGap: '35%',
        emphasis: { itemStyle: { color: darken(colors[optionIndex], 0.2) } },
        data: optionItemResult.map(period => {
          const optionData = period.options.find(op => op.name === option)
          return {
            value: optionData?.count || 0,
            name: formatPeriodName(period.groupName),
            groupName: period.groupName,
            answersSelected: optionData?.isOther ? itemsInOther : [option],
            label: isHorizontalBarChart
              ? {
                  show: optionIndex === 0,
                  position: [0, -14],
                  align: 'left',
                  formatter: '{b}',
                  fontFamily: typography.fontFamily,
                  fontSize: typography.body2.fontSize,
                  overflow: 'truncate',
                  width: widthOfChart ? widthOfChart : undefined,
                }
              : {
                  position: 'top',
                },
          }
        }),
        label: {
          show: false,
        },
      })) || [],
    [
      existingOptions,
      colors,
      optionItemResult,
      formatPeriodName,
      itemsInOther,
      isHorizontalBarChart,
      typography,
      widthOfChart,
    ],
  )

  const chartOptions: EChartsOption = useMemo(() => {
    // Only include series that have data
    const summaryHasSkippedData = statisticsSummary.resultForGroups.some(res => res.companiesAnsweredDontHaveThis > 0)
    const summaryHasNoResponseData = statisticsSummary.resultForGroups.some(res => res.companiesNotAnswered > 0)

    const dataSeries: BarSeriesOption[] = [
      ...optionSeries,
      ...(summaryHasSkippedData ? [questionSkippedSeries] : []),
      ...(summaryHasNoResponseData ? [noResponseSeries] : []),
    ]

    const xAxis = isHorizontalBarChart ? (valueAxis as XAXisOption) : (categoryAxis as XAXisOption)
    const yAxis = isHorizontalBarChart ? ({ ...categoryAxis, show: false } as YAXisOption) : (valueAxis as YAXisOption)

    // Only include legend items for series that have data
    const legendItems = [
      ...(existingOptions || []),
      ...(summaryHasSkippedData ? [questionSkippedSeries.name as string] : []),
      ...(summaryHasNoResponseData ? [noResponseSeries.name as string] : []),
    ]

    return {
      tooltip: {
        axisPointer: {
          type: 'shadow',
        },
      },
      grid: {
        containLabel: !isHorizontalBarChart,
        left: isHorizontalBarChart ? 15 : 40,
        right: 7,
        top: 30,
        bottom: getLegendSpacing(isHorizontalBarChart ? 70 : 20, widthOfChart, legendItems),
      },
      color: colors,
      xAxis,
      yAxis,
      legend: {
        icon: 'circle',
        bottom: 0,
        left: 0,
        itemWidth: 10,
        width: '100%',
      },
      series: dataSeries,
    }
  }, [
    optionSeries,
    questionSkippedSeries,
    noResponseSeries,
    isHorizontalBarChart,
    valueAxis,
    categoryAxis,
    colors,
    typography.fontFamily,
    typography.body2.fontSize,
    widthOfChart,
    existingOptions,
    palette,
  ])

  return chartOptions
}
